import { Text } from "@musa.tech/design-system";
import { useCountUp } from "@/hooks/useCountUp";
import { fmtMass, fmtMassAverage } from "@/utils/formatMass";
import { fmtCompactNumber } from "@/utils/formatCompactNumber";
import { CompactValue } from "./CompactValue";
import { KpiCardShell } from "./KpiCardShell";
import { formatOneDecimal, safeAverage } from "./perCollectAverage";

interface TotalWeightCollectedCardProps {
  /** Realizado dos materiais medidos em kg (ver `splitTotalsByUnit`). */
  totalWeightKg: number;
  /** Coletas dos materiais em kg — divisor de "kg por coleta". */
  kgCollects: number;
  totalUnits: number;
  /** Realizado dos materiais medidos em litros, sem conversão (óleo e demais líquidos). */
  totalVolumeLiters: number;
  /** Coletas dos materiais em litros — divisor de "Lt por coleta". */
  ltCollects: number;
  totalCollects: number;
}

/**
 * Card 3 "Peso total coletado" — sem ícones (confirmado no Figma, node
 * 124:13026: só cards 1/2 têm Info). Peso usa fmtWeight (GR-548: sempre kg,
 * truncado, nunca arredonda pra cima); médias "por coleta" usam 1 casa
 * decimal, à parte — são texto secundário (neutral-900), não submétrica
 * (neutral-700 é só a linha "X itens/L coletados").
 */
export const TotalWeightCollectedCard = ({
  totalWeightKg,
  kgCollects,
  totalUnits,
  totalVolumeLiters,
  ltCollects,
  totalCollects,
}: TotalWeightCollectedCardProps) => {
  // Todos os números do card contam juntos, inclusive as médias por coleta —
  // que derivam dos totais animados. Como cada valor é `alvo × easing(t)`, com
  // o mesmo easing, as proporções entre eles ficam corretas em cada frame: o
  // card nunca exibe uma combinação impossível durante a contagem.
  const animatedWeightKg = useCountUp(totalWeightKg);
  const animatedUnits = useCountUp(totalUnits);
  const animatedVolumeLiters = useCountUp(totalVolumeLiters);

  // Número cheio até 1 milhão, "mi" a partir daí — nunca "K" (ver
  // formatMass.ts). O valor abreviado ganha o exato no tooltip (CompactValue).
  const { value: weightValue, unit: weightUnit } = fmtMass(animatedWeightKg);
  // Média usa fmtMassAverage: `fmtMass` trunca para inteiro e "10 kg por
  // coleta" esconderia a diferença entre 10,0 e 10,9.
  // Cada média divide pelas coletas do próprio grupo: o óleo (Lt) não entra no "kg por coleta", e o
  // papel (kg) não entra no "Lt por coleta".
  const weightPerCollect = fmtMassAverage(safeAverage(animatedWeightKg, kgCollects));
  const unitsPerCollect = formatOneDecimal(safeAverage(animatedUnits, totalCollects));
  const volumePerCollect = formatOneDecimal(
    safeAverage(animatedVolumeLiters, ltCollects),
  );

  return (
    <KpiCardShell
      label="Peso total coletado"
      header={
        <div className="flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-baseline gap-1">
            <Text className="min-w-0 text-2xl font-semibold leading-9 tracking-normal text-neutral-900">
              <CompactValue exact={totalWeightKg} unit="kg">
                {weightValue}
              </CompactValue>
            </Text>
            {/* Símbolo de unidade não pluraliza (SI/ABNT). O Figma escreve
                "kgs" (node 124:13026) — divergência consciente. */}
            <Text
              variant="heading"
              size="small"
              className="shrink-0 tracking-[1px] text-neutral-900"
            >
              {weightUnit}
            </Text>
          </div>
          <Text className="min-w-0 text-xs leading-4 text-neutral-900">
            {weightPerCollect} por coleta
          </Text>
        </div>
      }
      rows={
        <>
          <div className="flex items-center justify-between gap-2">
            <Text variant="label" size="small" className="min-w-0 text-neutral-700">
              <CompactValue exact={totalUnits} unit="itens">
                {fmtCompactNumber(animatedUnits)}
              </CompactValue>{" "}
              itens coletados
            </Text>
            <Text className="min-w-0 text-xs leading-4 text-neutral-900">
              {unitsPerCollect} itens por coleta
            </Text>
          </div>
          <div className="flex items-center justify-between gap-2">
            <Text variant="label" size="small" className="min-w-0 text-neutral-700">
              <CompactValue exact={totalVolumeLiters} unit="Lt">
                {fmtCompactNumber(animatedVolumeLiters)}
              </CompactValue>{" "}
              Lt volume coletado
            </Text>
            <Text className="min-w-0 text-xs leading-4 text-neutral-900">
              {volumePerCollect} Lt por coleta
            </Text>
          </div>
        </>
      }
    />
  );
};
